import { useState, useEffect, useContext, useCallback, type ReactElement } from 'react'
import Stack from '@mui/material/Stack'
import Paper from '@mui/material/Paper'
import CircularProgress from '@mui/material/CircularProgress'
import { useQueries } from '@tanstack/react-query'
import { TripDetailsContext } from 'context/trips/TripDetailsContext'
import { combineQueries } from 'global/helpers/queries'
import KNMapLibre from 'components/KNMapLibre/KNMapLibre'
import {
  type MapMarker,
  MapMarkerState,
  type LocationGroupMapMarker,
  hasLocationGroup,
} from 'components/KNMapLibre/types'
import { groupStopsByLocation } from 'screens/TripDashboard/TripDashboard.helpers'
import type { TripData, StopsLocationGroup } from 'screens/TripDashboard/TripDashboard.types'
import {
  getStopTooltip,
  getFirstPositionMarker,
  getDwellLocations,
  getDwellMarkers,
  getGeofenceMarkers,
  getVehicleMarker,
  getParkingLocationMarkers,
} from './SequenceMapLibreView'
import {
  tripGeofencesQueryOptions,
  tripShortestRoutesQueryOptions,
  tripVehiclePositionsQueryOptions,
} from 'screens/TripDetails/TripDetails.service'
import {
  positionDataTransformer,
  groupGeoPointsBySpeed,
} from 'screens/TripDetails/TripDetails.helpers'
import type {
  LegData,
  TripLogData,
  GeoPoint,
  GeoPointsGroup,
  ParkingLocation,
} from 'screens/TripDetails/TripDetails.types'

export interface LocationsMapViewProps {
  trip: TripData
  legs: LegData[]
  tripLogs: TripLogData[]
  parkingLocations?: ParkingLocation[]
  weblinkToken?: string
}

const combineMapQueries = combineQueries(['positions', 'shortestRoutes', 'geofences'])

const getLocationMarkers = (groups: StopsLocationGroup[], tripLogs: TripLogData[]): LocationGroupMapMarker[] =>
  groups.reduce((markers: LocationGroupMapMarker[], group: StopsLocationGroup) => {
    if (!group.stopLegPairs[0]) {
      return markers
    }
    if (group.stopLegPairs[0].stop.geoPoint) {
      markers.push({
        id: group.stopLegPairs[0].stop.wayPointCid,
        latitude: group.stopLegPairs[0].stop.geoPoint.latitude,
        longitude: group.stopLegPairs[0].stop.geoPoint.longitude,
        geofenceRadius: group.stopLegPairs[0].stop.geofenceRadius ?? 500,
        geofencePolygon: group.stopLegPairs[0].stop.polygonRegion,
        tooltip: getStopTooltip(group, tripLogs),
        locationGroup: group,
      })
    }
    return markers
  }, [])

const LocationsMapLibreView = ({
  trip,
  legs,
  tripLogs,
  parkingLocations,
  weblinkToken,
}: LocationsMapViewProps): ReactElement => {
  const [tripDetailsState, tripDetailsDispatch] = useContext(TripDetailsContext)
  const mapQueries = useQueries({
    queries: [
      tripVehiclePositionsQueryOptions(trip.entityId, weblinkToken),
      tripShortestRoutesQueryOptions(trip.entityId, weblinkToken),
      tripGeofencesQueryOptions(trip.entityId, weblinkToken),
    ],
    combine: combineMapQueries,
  })
  // markers generated from props data
  const [staticMarkers, setStaticMarkers] = useState<MapMarker[]>([])
  // markers generated from remote calls
  const [dynamicMarkers, setDynamicMarkers] = useState<MapMarker[]>([])
  // all markers combined
  const [markers, setMarkers] = useState<MapMarker[]>([])
  const [geoPoints, setGeoPoints] = useState<GeoPoint[]>([])
  const [groupedGeoPoints, setGroupedGeoPoints] = useState<GeoPointsGroup[]>([])
  const [shortestRoutesGeoPoints, setShortestRoutesGeoPoints] = useState<GeoPoint[][]>([])

  useEffect(() => {
    const groupedStops = groupStopsByLocation(legs)
    setStaticMarkers([
      ...getLocationMarkers(groupedStops, tripLogs),
      ...getParkingLocationMarkers(parkingLocations),
    ])
  }, [legs, parkingLocations])

  useEffect(() => {
    if (!mapQueries.isSuccess) {
      return
    }
    const vehiclePositions = positionDataTransformer(mapQueries.data.positions)
    setGeoPoints(vehiclePositions)
    setGroupedGeoPoints(groupGeoPointsBySpeed(vehiclePositions))
    setShortestRoutesGeoPoints(mapQueries.data.shortestRoutes.map((route) => route.coordinates))
    if (vehiclePositions.length === 0) {
      setDynamicMarkers([])
      return
    }
    const dwellLocations = getDwellLocations(vehiclePositions)
    const dynamicMarkers: MapMarker[] = [
      getFirstPositionMarker(vehiclePositions[0]!),
      ...getGeofenceMarkers(mapQueries.data.geofences, tripLogs),
      ...getDwellMarkers(dwellLocations),
    ]
    const lastVehiclePosition = vehiclePositions[vehiclePositions.length - 1]!
    if (trip.assignedTrailer) {
      dynamicMarkers.push(getVehicleMarker(trip.assignedTrailer, lastVehiclePosition))
    } else if (trip.assignedTruck) {
      dynamicMarkers.push(getVehicleMarker(trip.assignedTruck, lastVehiclePosition))
    }
    setDynamicMarkers(dynamicMarkers)
  }, [mapQueries, trip, tripLogs])

  useEffect(() => {
    setMarkers([
      ...staticMarkers,
      ...dynamicMarkers,
    ])
  }, [staticMarkers, dynamicMarkers])

  useEffect(() => {
    setMarkers(markers.map((marker) => {
      marker.state = MapMarkerState.Default
      if (hasLocationGroup(marker)) {
        marker.state = tripDetailsState.activeStopsGroup === marker.locationGroup.id ? MapMarkerState.Active : MapMarkerState.Default
      }
      return marker
    }))
  }, [tripDetailsState.activeStopsGroup])

  const handleMarkerClick = useCallback((marker: MapMarker) => {
    if (hasLocationGroup(marker)) {
      tripDetailsDispatch({
        type: 'setActiveStopsGroup',
        payload: marker.locationGroup.id,
      })
      tripDetailsDispatch({
        type: 'setDetailsView',
        payload: 'stops',
      })
    }
  }, [])

  return (
    <Paper
      sx={{
        flexGrow: 1,
        overflow: 'hidden',
        backgroundColor: '#e9ecef',
      }}
    >
      {mapQueries.isPending ? (
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            width: '100%',
            height: '100%',
          }}
        >
          <CircularProgress size={64} color="primary" />
        </Stack>
      ) : (
        <KNMapLibre
          markers={markers}
          geoPoints={geoPoints}
          groupedGeoPoints={groupedGeoPoints}
          withHeading
          onMarkerClick={handleMarkerClick}
          shortestRoutes={shortestRoutesGeoPoints}
          focusGeoPoint={tripDetailsState.focusGeoPoint}
        />
      )}
    </Paper>
  )
}

export default LocationsMapLibreView
